import PropTypes from 'prop-types'
import { useState } from 'react'
import { format, parse, isValid } from 'date-fns'
import { Calendar as CalendarIcon } from 'lucide-react'
import { Calendar } from '@components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover'
import { Button } from '@components/ui/button'
import { Label } from '@components/ui/label'
import { cn } from 'lib/utils'

const DATE_FORMAT = 'MM/dd/yyyy'

// date-fns v2+ uses Unicode tokens (`dd`, `yyyy`). Many callers still pass the
// legacy dayjs/moment tokens (`DD`, `YYYY`) via DATE_FORMATS.ISO_DATE_STRING.
// Normalize so both work.
const toUnicodeFormat = (fmt) => (fmt || DATE_FORMAT).replace(/DD/g, 'dd').replace(/YYYY/g, 'yyyy')

export const AdasDatePicker = ({
  disabled,
  label,
  value,
  onChange,
  disableFuture,
  disablePast,
  format: fmt = DATE_FORMAT,
  className,
  minDate,
  maxDate,
  ...rest
}) => {
  const [open, setOpen] = useState(false)
  const normalizedFmt = toUnicodeFormat(fmt)

  const parsedDate = value
    ? parse(typeof value === 'string' ? value : format(new Date(value), normalizedFmt), normalizedFmt, new Date())
    : undefined
  const validDate = parsedDate && isValid(parsedDate) ? parsedDate : undefined

  const handleSelect = (selected) => {
    if (selected && onChange) {
      onChange(selected)
    }
    setOpen(false)
  }

  const disabledMatcher = []
  if (disableFuture) disabledMatcher.push({ after: new Date() })
  if (disablePast) disabledMatcher.push({ before: new Date() })
  if (minDate) disabledMatcher.push({ before: new Date(minDate) })
  if (maxDate) disabledMatcher.push({ after: new Date(maxDate) })

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {label && <Label className='text-xs font-medium text-text-primary'>{label}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant='outline'
            disabled={disabled}
            className={cn('w-full justify-start font-normal border-custom-borderInput text-left', !validDate && 'text-text-secondary')}
          >
            <CalendarIcon className='mr-2 h-4 w-4' />
            {validDate ? format(validDate, normalizedFmt) : <span>Pick a date</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-auto p-0' align='start'>
          <Calendar
            mode='single'
            selected={validDate}
            onSelect={handleSelect}
            disabled={disabledMatcher.length ? disabledMatcher : undefined}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}

AdasDatePicker.propTypes = {
  disabled: PropTypes.bool,
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  onChange: PropTypes.func,
  disableFuture: PropTypes.bool,
  disablePast: PropTypes.bool,
  format: PropTypes.string,
  className: PropTypes.string,
  minDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  maxDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string])
}
